import React from "react";
import { Button, Card, Empty, Popconfirm, Table, Tag, Tooltip, Typography } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { formatPrice } from "functions/formatters";
import { PRICE_IMPORT_RUN_STATUS } from "functions/firestore/priceImport";

const { Text } = Typography;

const STATUS_COLORS = {
  [PRICE_IMPORT_RUN_STATUS.PENDING]: "processing",
  [PRICE_IMPORT_RUN_STATUS.COMPLETED]: "green",
  [PRICE_IMPORT_RUN_STATUS.COMPLETED_WITH_ISSUES]: "orange",
  [PRICE_IMPORT_RUN_STATUS.FAILED]: "red",
  [PRICE_IMPORT_RUN_STATUS.ROLLED_BACK]: "default",
  [PRICE_IMPORT_RUN_STATUS.ROLLBACK_WITH_ISSUES]: "orange",
};

const ROLLBACKABLE = [PRICE_IMPORT_RUN_STATUS.COMPLETED, PRICE_IMPORT_RUN_STATUS.COMPLETED_WITH_ISSUES];

const HistoryCard = ({ runs, loading, rollbackRunId, onRollback }) => {
  const { t } = useTranslation();

  const columns = [
    {
      title: t("price_import.history.column_date"),
      dataIndex: "createdAt",
      width: 150,
      render: (createdAt) => (createdAt ? dayjs.unix(createdAt).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: t("price_import.history.column_user"),
      dataIndex: "createdBy",
      render: (createdBy) => createdBy || "-",
    },
    {
      title: t("price_import.history.column_file"),
      dataIndex: "fileName",
      render: (fileName) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {fileName || "-"}
        </Text>
      ),
    },
    {
      title: t("price_import.history.column_applied"),
      dataIndex: "appliedCount",
      width: 110,
      align: "right",
      render: (appliedCount, run) => `${appliedCount || 0} / ${run.lineCount || 0}`,
    },
    {
      title: t("price_import.history.column_status"),
      dataIndex: "status",
      width: 220,
      render: (status, run) => (
        <>
          <Tag color={STATUS_COLORS[status] || "default"}>{t(`price_import.history.status_${status}`)}</Tag>
          {run.linesStored === false && (
            <Tooltip title={t("price_import.history.lines_not_stored")}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ⓘ
              </Text>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      align: "right",
      render: (_, run) => {
        const canRollback = ROLLBACKABLE.includes(run.status) && !!run.lines?.length;
        if (!canRollback) return null;
        return (
          <Popconfirm
            title={t("price_import.result.rollback_confirm", { count: run.lines.length })}
            okText={t("yes")}
            cancelText={t("no")}
            onConfirm={() => onRollback(run)}
          >
            <Tooltip title={t("price_import.history.rollback_tooltip")}>
              <Button
                size="small"
                danger
                type="text"
                icon={<UndoOutlined />}
                loading={rollbackRunId === run.id}
              />
            </Tooltip>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Card style={{ marginTop: 16 }} title={t("price_import.history.title")}>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={runs}
        pagination={false}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("price_import.history.empty")} />,
        }}
        expandable={{
          rowExpandable: (run) => !!run.lines?.length,
          expandedRowRender: (run) => (
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {run.lines.map((line, index) => (
                <div key={`${run.id}-${line.productId || index}`} style={{ fontSize: 12, padding: "2px 0" }}>
                  <Text type="secondary">{line.code}</Text> {line.name} — € {formatPrice(line.oldPrice)} → €{" "}
                  {formatPrice(line.newPrice)}
                </div>
              ))}
            </div>
          ),
        }}
      />
    </Card>
  );
};

export default HistoryCard;
